import type { BackendModule, InitOptions, ReadCallback, Services } from 'i18next';
import { mergeTranslations, Translations } from './translations.business';

type BackendConstructor = new (
  services: Services,
  backendOptions: object,
  i18nextOptions: InitOptions
) => BackendModule;

export interface BundledFallbackBackendOptions {
  remote: BackendConstructor;
  remoteOptions: object;
  bundled: BackendModule;
}

const toError = (error: Error | string): Error =>
  typeof error === 'string' ? new Error(error) : error;

const readFrom = (backend: BackendModule, language: string, namespace: string) =>
  new Promise<Translations>((resolve, reject) => {
    backend.read(language, namespace, (error, data) => {
      if (error) {
        reject(toError(error));
        return;
      }
      resolve((data ?? {}) as Translations);
    });
  });

// The language service may lag behind the app: labels missing remotely come from the bundled copy.
export class BundledFallbackBackend implements BackendModule<BundledFallbackBackendOptions> {
  static readonly type = 'backend';
  readonly type = 'backend';
  private remote!: BackendModule;
  private bundled!: BackendModule;

  init(services: Services, options: BundledFallbackBackendOptions, i18nextOptions: InitOptions) {
    this.remote = new options.remote(services, options.remoteOptions, i18nextOptions);
    this.bundled = options.bundled;
    this.bundled.init?.(services, {}, i18nextOptions);
  }

  read(language: string, namespace: string, callback: ReadCallback) {
    void Promise.allSettled([
      readFrom(this.remote, language, namespace),
      readFrom(this.bundled, language, namespace),
    ]).then(([remote, bundled]) => {
      if (remote.status === 'rejected' && bundled.status === 'rejected') {
        callback(remote.reason, false);
        return;
      }
      callback(
        null,
        mergeTranslations(
          bundled.status === 'fulfilled' ? bundled.value : {},
          remote.status === 'fulfilled' ? remote.value : {}
        )
      );
    });
  }
}
